import axios from "axios";

import { isMerchantType } from "./Data";

// Cross-tab channel. The edit tab writes the saved user id here; every flow tab
// listening for that id re-reads the user and refreshes its own snapshot. Same
// localStorage + `storage` idiom the Google Calendar callback tab uses — there
// is no BroadcastChannel anywhere in this app, and React Query runs with
// refetchOnWindowFocus disabled globally.
export const USER_UPDATED_KEY = "rc-user-updated";

/**
 * Which Users CRM tab a user belongs to. The deep link carries it so the list
 * behind the drawer is the one the admin expects, and so `/users/:type?` keeps
 * matching as it does today.
 */
export const userEditTypeSegment = (user) => {
  const type = typeof user === "string" ? user : user?.type;
  if (type === "expert" || type === "concierge") return "expert";
  if (isMerchantType(type)) return "merchant";
  return "client";
};

/**
 * URL-addressable user edit: the existing Users CRM route plus `?userId=`,
 * which UsersPage hydrates through GET /api/users/{id} and opens in the
 * existing detail drawer on its Contact tab.
 */
export const userEditUrl = (user) => {
  const id = typeof user === "object" && user !== null ? user.id : user;
  return `/users/${userEditTypeSegment(user)}?userId=${id}`;
};

export const openUserEditTab = (user) => {
  const id = typeof user === "object" && user !== null ? user.id : user;
  if (!id) return;
  window.open(userEditUrl(user), "_blank");
};

/**
 * Tell every other tab that this user changed. The timestamp is what makes the
 * value differ between two saves of the same user — `storage` only fires when
 * the stored value actually changes.
 */
export const notifyUserUpdated = (user) => {
  const id = typeof user === "object" && user !== null ? user.id : user;
  if (!id) return;
  try {
    localStorage.setItem(
      USER_UPDATED_KEY,
      JSON.stringify({ id: Number(id), at: Date.now() })
    );
  } catch (e) {
    // Private mode / storage disabled — the edit still saved, it just cannot
    // be announced. Focus refresh in useUserUpdates covers this case.
  }
};

export const parseUserUpdatedValue = (value) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed?.id ? Number(parsed.id) : null;
  } catch (e) {
    return null;
  }
};

/** GET /api/users/{id} — responds `{ user: {...} }`. */
export const fetchUserById = async (id) => {
  const { data } = await axios.get(`/api/users/${id}`);
  return data?.user ?? null;
};
